import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import PostListItem from 'flarum/forum/components/PostListItem';
import CommentPost from 'flarum/forum/components/CommentPost';

/**
 * Long posts in lists (a member's profile, post search results) are cut to a few lines with a fade,
 * and a "Read the full post" link opens the post in its discussion, as Reddit and X do. Short posts
 * show in full. The link is part of the item (not added to Flarum's own post markup), so redraws keep it.
 * Tapping a picture in these lists opens the post too, instead of enlarging the picture.
 */
const limit = () => window.innerWidth < 768 ? 260 : 340; // px of post shown before cutting
const LEEWAY = 80; // don't cut a post for the sake of a couple of lines

function measure(item) {
  const root = item.element;
  const body = root && root.querySelector('.Post-body');
  if (!root || !body) return;
  const long = body.scrollHeight > limit() + LEEWAY;
  root.classList.toggle('VouchaClamp-on', long);
  // line the link up with the post text, whatever the layout (avatar column on desktop, none on phones)
  const link = root.querySelector('.VouchaClamp-more');
  if (link && long) link.style.marginLeft = Math.max(0, body.getBoundingClientRect().left - root.getBoundingClientRect().left) + 'px';
  // pictures arriving later make a post taller: check again when each loads
  body.querySelectorAll('img').forEach(img => {
    if (!img.complete && !img.dataset.vouchaClamp) {
      img.dataset.vouchaClamp = '1';
      img.addEventListener('load', () => measure(item), {
        once: true
      });
    }
  });
}

/** A picture in a listed post opens the post: caught before the picture viewer (Fancybox) sees the tap */
function picturesOpenPost(item) {
  const root = item.element;
  if (!root || root.dataset.vouchaPics) return;
  root.dataset.vouchaPics = '1';
  root.addEventListener('click', e => {
    const img = e.target && e.target.closest && e.target.closest('.Post-body img');
    if (!img || img.closest('.emoji, .Avatar')) return;
    const post = item.attrs.post;
    if (!post) return;
    e.preventDefault();
    e.stopPropagation();
    const href = app.route.post(post);
    if (e.ctrlKey || e.metaKey) window.open(href, '_blank');else m.route.set(href);
  }, true);
}
export default function vouchaPostClamp() {
  extend(PostListItem.prototype, 'view', function (vdom) {
    const post = this.attrs.post;
    if (!vdom || !Array.isArray(vdom.children) || !post) return;
    const href = app.route.post(post);
    vdom.children.push(m("a", {
      className: "VouchaClamp-more",
      href: href,
      onclick: e => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        m.route.set(href);
      }
    }, "Read the full post ", m("i", {
      className: "fas fa-arrow-right",
      "aria-hidden": "true"
    })));
  });
  extend(PostListItem.prototype, 'oncreate', function () {
    measure(this);
    picturesOpenPost(this);
  });
  extend(PostListItem.prototype, 'onupdate', function () {
    measure(this);
  });
}

/**
 * Replies in a discussion: a long one shows about three lines with a fade and "Read more", which opens it
 * in place ("Show less" folds it again), as Reddit and X do. The opening post always shows in full, and so
 * does a reply that is only a little over three lines. Opened replies stay open while the page is up.
 */
const REPLY_LINES = 3;
const REPLY_LEEWAY_LINES = 2; // a reply up to 5 lines just shows
const openReplies = new Set();
function replyLineHeight(body) {
  const lh = parseFloat(getComputedStyle(body).lineHeight);
  return isFinite(lh) ? lh : 23;
}
function measureReply(item) {
  const root = item.element;
  const post = item.attrs.post;
  if (!root || !post) return;
  const body = root.querySelector('.Post-body');
  const inStream = !!root.closest('.DiscussionPage .PostStream');
  if (!body || !inStream || post.number() === 1 || root.classList.contains('editing')) {
    root.classList.remove('VouchaReplyClamp-on', 'is-open');
    return;
  }
  const line = replyLineHeight(body);
  body.style.setProperty('--voucha-reply-max', Math.round(line * REPLY_LINES) + 'px');
  const long = body.scrollHeight > line * (REPLY_LINES + REPLY_LEEWAY_LINES);
  root.classList.toggle('VouchaReplyClamp-on', long);
  root.classList.toggle('is-open', long && openReplies.has(post.id()));
  body.querySelectorAll('img').forEach(img => {
    if (!img.complete && !img.dataset.vouchaReply) {
      img.dataset.vouchaReply = '1';
      img.addEventListener('load', () => measureReply(item), {
        once: true
      });
    }
  });
}
function findChild(node, test, depth) {
  if (depth === void 0) {
    depth = 0;
  }
  if (!node || depth > 4) return null;
  const kids = Array.isArray(node.children) ? node.children : [];
  for (const k of kids) {
    if (k && typeof k === 'object') {
      if (test(k)) return {
        parent: node,
        child: k
      };
      const hit = findChild(k, test, depth + 1);
      if (hit) return hit;
    }
  }
  return null;
}
export function vouchaReplyClamp() {
  extend(CommentPost.prototype, 'view', function (vdom) {
    const post = this.attrs.post;
    if (!post || post.number() === 1 || !vdom) return;
    // between the text and the actions, so it sits inside the reply's card
    const hit = findChild(vdom, v => v.tag === 'aside' && v.attrs && v.attrs.className === 'Post-actions');
    if (!hit || !Array.isArray(hit.parent.children)) return;
    const open = openReplies.has(post.id());
    const button = m("button", {
      type: "button",
      className: "VouchaReplyClamp-more",
      "aria-expanded": open ? 'true' : 'false',
      onclick: e => {
        e.preventDefault();
        if (openReplies.has(post.id())) openReplies.delete(post.id());else openReplies.add(post.id());
        const root = e.currentTarget.closest('.CommentPost');
        if (root) root.classList.toggle('is-open', openReplies.has(post.id()));
        // folding a long reply: keep its start in view
        if (!openReplies.has(post.id()) && root && root.getBoundingClientRect().top < 0) {
          window.scrollBy({
            top: root.getBoundingClientRect().top - 90
          });
        }
      }
    }, open ? 'Show less' : 'Read more');
    hit.parent.children.splice(hit.parent.children.indexOf(hit.child), 0, button);
  });
  extend(CommentPost.prototype, 'oncreate', function () {
    measureReply(this);
  });
  extend(CommentPost.prototype, 'onupdate', function () {
    measureReply(this);
  });
}